import { QuickPassProfile } from '../../_types/quickpass-profile'
import { FieldCheck, ProfileCompletenessSummary } from './types'

/**
 * Computes how much of the renter's profile is filled out. Returns a
 * percent + breakdown so a UI can list what's missing as a hint. Each
 * preference axis counts equally — name, cities, budget, bedrooms,
 * bathrooms, place types, dwelling scope.
 *
 * @param {QuickPassProfile | null} profile - the renter profile
 * @returns {ProfileCompletenessSummary} the percent + missing fields
 */
const evaluateProfileCompleteness = (
  profile: QuickPassProfile | null,
): ProfileCompletenessSummary => {
  if (profile === null) {
    return {
      percent: 0,
      filledCount: 0,
      totalCount: 0,
      missing: [],
    }
  }

  const preferences = profile.unitPreferences
  const checks: FieldCheck[] = [
    { label: 'First name', isFilled: profile.firstName.trim() !== '' },
    { label: 'Last name', isFilled: profile.lastName.trim() !== '' },
    { label: 'Cities', isFilled: preferences.cities.length > 0 },
    {
      label: 'Budget',
      isFilled: preferences.priceMin !== null || preferences.priceMax !== null,
    },
    { label: 'Bedrooms', isFilled: preferences.bedroomCounts.length > 0 },
    { label: 'Bathrooms', isFilled: preferences.bathroomCounts.length > 0 },
    { label: 'Place types', isFilled: preferences.propertyTypes.length > 0 },
    {
      label: 'Whole or unit',
      isFilled: preferences.dwellingScope !== 'no-preference',
    },
  ]

  const filled = checks.filter(check => check.isFilled === true)
  const total = checks.length
  const percent = Math.round((filled.length / total) * 100)
  const missing = checks
    .filter(check => check.isFilled === false)
    .map(check => check.label)

  return {
    percent,
    filledCount: filled.length,
    totalCount: total,
    missing,
  }
}

export { evaluateProfileCompleteness }
